import { strFromU8, unzipSync } from "fflate";

export const WEB_ARCHIVE_ENGAGEMENT_ID = "offline-archive";

function normalizeZipPath(p: string): string {
  return p.replace(/\\/g, "/").replace(/^\/+/, "");
}

/** Detect folder prefix so that `${prefix}juggernaut/` and `${prefix}client/` exist. */
function detectPrefix(files: Map<string, Uint8Array>): string {
  const keys = [...files.keys()];
  for (const k of keys) {
    const m = k.match(/^(.+)\/juggernaut\//);
    if (!m) continue;
    const candidate = m[1];
    const prefix = candidate ? `${candidate}/` : "";
    const hasClient = keys.some((x) => x.startsWith(`${prefix}client/`));
    if (hasClient) return prefix;
  }
  return "";
}

export class ZipEngagementFs {
  readonly files: ReadonlyMap<string, Uint8Array>;
  /** e.g. "Beta Test2/" or "" when zip root is client+juggernaut */
  readonly pathPrefix: string;
  /** Engagement folder name used as FY_YEAR / SharePoint root (e.g. "Beta Test2") */
  readonly fyYear: string;

  private constructor(
    files: Map<string, Uint8Array>,
    pathPrefix: string,
    fyYear: string,
  ) {
    this.files = files;
    this.pathPrefix = pathPrefix;
    this.fyYear = fyYear;
  }

  static fromArrayBuffer(buffer: ArrayBuffer): ZipEngagementFs {
    const raw = unzipSync(new Uint8Array(buffer), { filter: () => true });
    const files = new Map<string, Uint8Array>();
    for (const [path, data] of Object.entries(raw)) {
      if (path.endsWith("/")) continue;
      files.set(normalizeZipPath(path), data);
    }

    const pathPrefix = detectPrefix(files);

    let fyYear = pathPrefix.replace(/\/$/, "");
    const details = readEngagementDetailsRaw(files, pathPrefix);
    if (details?.engagement_name && String(details.engagement_name).trim()) {
      fyYear = String(details.engagement_name).trim();
    } else if (!fyYear) {
      fyYear = "Engagement";
    }

    return new ZipEngagementFs(files, pathPrefix, fyYear);
  }

  getEngagementDetails(): Record<string, unknown> | null {
    return readEngagementDetailsRaw(new Map(this.files), this.pathPrefix);
  }

  /** Try paths SharePoint would use: juggernaut/X, {fy}/juggernaut/X, prefixed */
  resolveExisting(rel: string): string | null {
    const relNorm = normalizeZipPath(rel);
    const candidates = [
      `${this.pathPrefix}${relNorm}`,
      relNorm,
      `${this.fyYear}/${relNorm}`,
      `${this.pathPrefix}${this.fyYear}/${relNorm}`,
    ];
    for (const c of candidates) {
      if (this.files.has(c)) return c;
    }
    const lower = relNorm.toLowerCase();
    for (const key of this.files.keys()) {
      if (key.toLowerCase() === lower) return key;
      if (key.toLowerCase().endsWith("/" + lower)) return key;
    }
    return null;
  }

  readText(rel: string): string | null {
    const key = this.resolveExisting(rel);
    if (!key) return null;
    const u8 = this.files.get(key);
    if (!u8) return null;
    try {
      return strFromU8(u8, false);
    } catch {
      return new TextDecoder("utf-8", { fatal: false }).decode(u8);
    }
  }

  readJson<T = unknown>(rel: string): T | null {
    const t = this.readText(rel);
    if (t == null || !t.trim()) return null;
    try {
      return JSON.parse(t) as T;
    } catch {
      return null;
    }
  }

  readJuggernautJson<T = unknown>(fileName: string): T | null {
    return this.readJson<T>(`juggernaut/${fileName}`);
  }

  readEngagementRootJson<T = unknown>(fileName: string): T | null {
    return this.readJson<T>(fileName);
  }

  readRbinJson<T = unknown>(fileName: string): T | null {
    return this.readJson<T>(`rbin/${fileName}`);
  }

  getBytes(rel: string): Uint8Array | null {
    const key = this.resolveExisting(rel);
    if (!key) return null;
    return this.files.get(key) ?? null;
  }
}

function readTextFromMap(
  files: Map<string, Uint8Array>,
  pathPrefix: string,
  rel: string,
): string | null {
  const relNorm = normalizeZipPath(rel);
  for (const c of [`${pathPrefix}${relNorm}`, relNorm]) {
    const u8 = files.get(c);
    if (u8) {
      try {
        return strFromU8(u8, false);
      } catch {
        return new TextDecoder("utf-8", { fatal: false }).decode(u8);
      }
    }
  }
  return null;
}

function readEngagementDetailsRaw(
  files: Map<string, Uint8Array>,
  pathPrefix: string,
): Record<string, unknown> | null {
  const t = readTextFromMap(files, pathPrefix, "EngagementDetails.json");
  if (t == null || !t.trim()) return null;
  try {
    return JSON.parse(t) as Record<string, unknown>;
  } catch {
    return null;
  }
}
